import { useEffect, useState } from 'react';
import {
  loadQontoOverview,
  startQontoConnect,
  createMandate,
  syncQontoStatuses,
  triggerCollect,
  runMonthlyCollect,
  type QontoOverview,
} from '../../lib/qontoService';
import { mandateLabel, collectionOutcome, centsToQontoAmount } from '../../lib/qonto';

// Panneau super admin — Prélèvement SEPA Qonto : statut connexion + mandats par resto + collectes.

const euros = (cents: number, cur = 'EUR') => {
  const { value } = centsToQontoAmount(cents, cur === 'CHF' ? 'CHF' : 'EUR');
  return `${value} ${cur}`;
};

const outcomeBadge: Record<string, { label: string; cls: string }> = {
  settled: { label: 'Encaissé', cls: 'bg-emerald-100 text-emerald-700' },
  pending: { label: 'En cours', cls: 'bg-slate-100 text-slate-600' },
  failed: { label: 'Échec', cls: 'bg-red-100 text-red-700' },
  reversed: { label: 'Rejeté/Remboursé', cls: 'bg-amber-100 text-amber-700' },
};

export default function QontoSddPanel() {
  const [data, setData] = useState<QontoOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [mandateBusy, setMandateBusy] = useState('');
  const [rowMsg, setRowMsg] = useState<Record<string, string>>({});
  const [syncing, setSyncing] = useState(false);
  const [collectAmt, setCollectAmt] = useState<Record<string, string>>({});
  const [collectBusy, setCollectBusy] = useState('');
  const [monthlyBusy, setMonthlyBusy] = useState(false);
  const [mandateAmt, setMandateAmt] = useState<Record<string, string>>({});

  const refresh = () => loadQontoOverview().then((res) => { setData(res); setLoading(false); });

  useEffect(() => { refresh(); }, []);

  const genMandate = async (slug: string) => {
    const euros = parseFloat((mandateAmt[slug] || '').replace(',', '.'));
    if (!euros || euros <= 0) {
      setRowMsg((m) => ({ ...m, [slug]: 'Saisis le montant du 1ᵉʳ prélèvement (€) — il déclenche l’email de signature.' }));
      return;
    }
    setMandateBusy(slug);
    const res = await createMandate(slug, Math.round(euros * 100));
    setMandateBusy('');
    setRowMsg((m) => ({ ...m, [slug]: res.ok ? '✅ Mandat créé — email de signature envoyé au resto.' : (res.error ?? 'Échec.') }));
    if (res.ok) refresh();
  };

  const sync = async () => {
    setSyncing(true);
    const res = await syncQontoStatuses();
    setSyncing(false);
    if (res.ok) refresh();
    else setMsg(res.error ?? 'Synchronisation impossible.');
  };

  const runMonthly = async () => {
    setMonthlyBusy(true);
    setMsg('');
    const res = await runMonthlyCollect();
    setMonthlyBusy(false);
    if (res.ok) {
      setMsg(`✅ Prélèvements ${res.tag} : ${res.collected} lancé(s)${res.failed ? `, ${res.failed} échec(s)` : ''}.`);
      refresh();
    } else {
      setMsg(res.error ?? 'Prélèvement mensuel impossible.');
    }
  };

  const collect = async (slug: string) => {
    const euros = parseFloat((collectAmt[slug] || '').replace(',', '.'));
    if (!euros || euros <= 0) {
      setRowMsg((m) => ({ ...m, [slug]: 'Saisis un montant € > 0.' }));
      return;
    }
    setCollectBusy(slug);
    const res = await triggerCollect(slug, Math.round(euros * 100));
    setCollectBusy('');
    setRowMsg((m) => ({ ...m, [slug]: res.ok ? `✅ Prélèvement de ${euros.toFixed(2)} € lancé.` : (res.error ?? 'Échec.') }));
    if (res.ok) refresh();
  };

  const connect = async () => {
    setBusy(true);
    setMsg('');
    const res = await startQontoConnect();
    setBusy(false);
    if (res.ok && res.url) {
      window.location.href = res.url; // redirection vers Qonto (retour via callback)
    } else {
      setMsg(res.error ?? 'Connexion impossible.');
    }
  };

  if (loading) return <p className="opacity-70">Chargement Qonto…</p>;

  const conn = data?.connection;
  const connected = !!conn?.connected;

  return (
    <div className="max-w-5xl">
      <h2 className="mb-1 text-xl font-black text-slate-900">🏦 Prélèvement SEPA (Qonto)</h2>
      <p className="mb-4 text-sm text-slate-500">
        Prélève automatiquement tes restos clients : mandat signé une fois, puis encaissement à chaque facture.
      </p>

      {data && !data.ok && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {data.error?.includes('qonto_sdd_overview')
            ? 'La base SDD n’est pas encore en place — exécute sql/44_qonto_sdd.sql.'
            : data.error}
        </div>
      )}

      {/* Connexion */}
      <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-xs font-bold uppercase text-slate-500">Compte Lil & Co</div>
            {connected ? (
              <div className="mt-1 text-sm text-slate-700">
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">Connecté</span>
                <span className="ml-2">{conn?.iban ? `${conn.iban.slice(0, 8)}…${conn.iban.slice(-4)}` : 'compte principal'}</span>
                <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[11px] uppercase text-slate-500">{conn?.env}</span>
              </div>
            ) : (
              <div className="mt-1 text-sm text-slate-500">Non connecté — autorise l’accès à ton compte Qonto.</div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {connected && (
              <button
                onClick={runMonthly}
                disabled={monthlyBusy}
                className="rounded-xl border border-blue-600 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-50"
              >
                {monthlyBusy ? '…' : '📅 Lancer les prélèvements du mois'}
              </button>
            )}
            <button
              onClick={connect}
              disabled={busy}
              className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {busy ? '…' : connected ? 'Reconnecter' : 'Connecter Qonto'}
            </button>
          </div>
        </div>
        {msg && <div className="mt-2 text-sm text-red-600">{msg}</div>}
      </div>

      {/* Mandats par resto */}
      <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-xs font-bold uppercase text-slate-500">Mandats par restaurant</div>
          <button
            onClick={sync}
            disabled={syncing || !connected}
            className="rounded-lg border border-slate-300 px-3 py-1 text-xs font-semibold hover:bg-slate-50 disabled:opacity-50"
          >
            {syncing ? '…' : '🔄 Rafraîchir les statuts'}
          </button>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-400">
              <th className="px-2 py-2">Restaurant</th>
              <th className="px-2 py-2">Statut mandat</th>
              <th className="px-2 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {(data?.tenants ?? []).map((t) => (
              <tr key={t.slug} className="border-b border-slate-100">
                <td className="px-2 py-2 font-semibold text-slate-700">{t.name}</td>
                <td className="px-2 py-2 text-slate-600">{mandateLabel(t.mandate_status)}</td>
                <td className="px-2 py-2 text-right">
                  {t.mandate_status === 'pending_signature' && t.sign_url ? (
                    <a href={t.sign_url} target="_blank" rel="noreferrer" className="text-xs font-semibold text-blue-600 underline">
                      Lien de signature
                    </a>
                  ) : t.mandate_status === 'approved' ? (
                    <div className="flex items-center justify-end gap-1">
                      <span className="mr-1 text-xs font-semibold text-emerald-600">✓ signé</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="€"
                        value={collectAmt[t.slug] ?? ''}
                        onChange={(e) => setCollectAmt((a) => ({ ...a, [t.slug]: e.target.value }))}
                        className="w-20 rounded border border-slate-300 px-1 py-1 text-right text-xs"
                      />
                      <button
                        onClick={() => collect(t.slug)}
                        disabled={collectBusy === t.slug}
                        className="rounded-lg bg-blue-600 px-2 py-1 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                      >
                        {collectBusy === t.slug ? '…' : 'Prélever'}
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-end gap-1">
                      <input
                        type="number" min="0" step="0.01" placeholder="1ᵉʳ € "
                        value={mandateAmt[t.slug] ?? ''}
                        onChange={(e) => setMandateAmt((a) => ({ ...a, [t.slug]: e.target.value }))}
                        className="w-20 rounded border border-slate-300 px-1 py-1 text-right text-xs"
                      />
                      <button
                        onClick={() => genMandate(t.slug)}
                        disabled={!connected || mandateBusy === t.slug}
                        className="rounded-lg border border-slate-300 px-2 py-1 text-xs font-semibold hover:bg-slate-50 disabled:opacity-50"
                      >
                        {mandateBusy === t.slug ? '…' : 'Créer le mandat'}
                      </button>
                    </div>
                  )}
                  {rowMsg[t.slug] && <div className="mt-1 text-[11px] text-slate-500">{rowMsg[t.slug]}</div>}
                </td>
              </tr>
            ))}
            {(data?.tenants ?? []).length === 0 && (
              <tr>
                <td colSpan={3} className="px-2 py-6 text-center text-slate-400">Aucun restaurant.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Collectes récentes */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="mb-3 text-xs font-bold uppercase text-slate-500">Encaissements récents</div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-400">
              <th className="px-2 py-2">Date</th>
              <th className="px-2 py-2">Référence</th>
              <th className="px-2 py-2 text-right">Montant</th>
              <th className="px-2 py-2 text-right">Statut</th>
            </tr>
          </thead>
          <tbody>
            {(data?.collections ?? []).map((c) => {
              const o = outcomeBadge[collectionOutcome(c.status)] ?? outcomeBadge.pending;
              return (
                <tr key={c.id} className="border-b border-slate-100">
                  <td className="px-2 py-2 text-slate-600">{c.collection_date ?? '—'}</td>
                  <td className="px-2 py-2 text-slate-600">{c.reference ?? '—'}</td>
                  <td className="px-2 py-2 text-right text-slate-700">{euros(c.amount_cents ?? 0, c.currency ?? 'EUR')}</td>
                  <td className="px-2 py-2 text-right">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${o.cls}`}>{o.label}</span>
                  </td>
                </tr>
              );
            })}
            {(data?.collections ?? []).length === 0 && (
              <tr>
                <td colSpan={4} className="px-2 py-6 text-center text-slate-400">Aucun encaissement pour l’instant.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
